"use client";

import React, { useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useSession } from "@/lib/auth-react";
import { hasAdminAccess, isFullAdmin } from "@/lib/role";
import {
  Home,
  Users,
  Calendar,
  Wallet,
  LogOut,
  Package,
  PackageOpen,
  BookOpen,
  MessageSquare,
  Mail,
  MailWarning,
  ChevronDown,
  User,
  Camera,
  GraduationCap,
  UserRound,
  Megaphone,
  Calculator,
  Cog,
  Coins,
  UserPlus,
  ScrollText,
  CircleUser,
  MonitorPlay,
  TicketPercent,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useLogout } from "@/hooks/useLogout";

// Navigation menu items for admin dashboard, grouped into visually
// separated sections in the desktop sidebar:
//   1. operations — daily admin work (home / users / purchases / bookings / financials / library)
//   2. content    — product loop items (feedback)
//   3. system     — reserved for future observability entries
// Home, Users, and Configuration are rendered separately by the desktop
// sidebar (Users and Configuration are expandable), so they don't appear
// in the grouped list below. Mobile nav consumes `adminMenuItems` (flat)
// — keep both in sync.
export type AdminMenuGroup = "operations" | "content" | "system";

export const adminMenuGroups: Array<{
  id: AdminMenuGroup;
  items: Array<{ title: string; labelKey: string; url: string; icon: typeof Home; fullAdminOnly?: boolean }>;
}> = [
  {
    id: "operations",
    items: [
      { title: "Purchases", labelKey: "purchases", url: "/dashboard/purchased-packages", icon: PackageOpen },
      { title: "Bookings", labelKey: "bookings", url: "/dashboard/bookings", icon: Calendar },
      { title: "Financials", labelKey: "financials", url: "/dashboard/financials", icon: Wallet },
      { title: "Discount codes", labelKey: "discountCodes", url: "/dashboard/pricing/create-promocode", icon: TicketPercent, fullAdminOnly: true },
      { title: "Test classroom", labelKey: "classroomTests", url: "/dashboard/classroom-tests", icon: MonitorPlay, fullAdminOnly: true },
      // Library is operational content admins curate alongside the
      // booking flow, not its own product-loop concern.
      { title: "Library", labelKey: "library", url: "/dashboard/library", icon: BookOpen },
    ],
  },
  {
    id: "content",
    items: [
      // Feedback sits above the content/system separator — admins open
      // it as part of the daily product loop, not "system observability".
      { title: "Feedback", labelKey: "feedback", url: "/dashboard/feedback", icon: MessageSquare },
    ],
  },
  // System group is currently empty after #743 (Newsletter removal).
  // The "system" id stays in the AdminMenuGroup union in case we add an
  // observability entry later; the render loop skips empty groups.
];

// Configuration — General / Communication / Financial Rules / Commission
// Tiers / Invites are tabs on /dashboard/settings/platform, exposed via
// the `?tab=` query string. Packages / Email Templates / Email Logs
// also live as tabs now (they used to be standalone pages — the old
// `/dashboard/packages` / `/dashboard/email-templates` /
// `/dashboard/email-logs` routes 308-redirect into the matching tab).
export const siteSettingsSubItems: Array<{
  title: string;
  labelKey: string;
  url: string;
  icon: typeof Home;
  tab?: string;
}> = [
  { title: "General", labelKey: "general", url: "/dashboard/settings/platform", tab: "general", icon: Cog },
  { title: "Communication", labelKey: "communication", url: "/dashboard/settings/platform", tab: "communication", icon: Megaphone },
  { title: "Financial Rules", labelKey: "financialRules", url: "/dashboard/settings/platform", tab: "financial-rules", icon: Coins },
  { title: "Commission Tiers", labelKey: "commissionTiers", url: "/dashboard/settings/platform", tab: "commission-tiers", icon: Calculator },
  { title: "Packages", labelKey: "packages", url: "/dashboard/settings/platform", tab: "packages", icon: Package },
  { title: "Invites", labelKey: "invites", url: "/dashboard/settings/platform", tab: "invites", icon: UserPlus },
  { title: "Email Templates", labelKey: "emailTemplates", url: "/dashboard/settings/platform", tab: "email-templates", icon: Mail },
  { title: "Email Logs", labelKey: "emailLogs", url: "/dashboard/settings/platform", tab: "email-logs", icon: MailWarning },
  // Read-only audit trail of sensitive actor-driven actions. Lives as a
  // tab on the platform settings page; backed by GET /api/audit-logs.
  { title: "Audit", labelKey: "audit", url: "/dashboard/settings/platform", tab: "audit", icon: ScrollText },
];

// Flat list — Home and Users prepended, then every grouped item, then a
// single "Configuration" parent. Sub-items are mounted under it on
// mobile via the same data source (Configuration drawer expands).
export const adminMenuItems = [
  { title: "Home", url: "/dashboard", icon: Home },
  { title: "Users", url: "/dashboard/users", icon: Users },
  ...adminMenuGroups.flatMap((g) => g.items),
  { title: "Settings", url: "/dashboard/settings/platform", icon: Cog },
];

// Users sub-items (rendered under the expandable "Users" parent)
const usersSubItems = [
  { title: "All users", labelKey: "allUsers", url: "/dashboard/users", icon: Users, key: "All" },
  { title: "Teachers", labelKey: "teachers", url: "/dashboard/users?tab=teachers", icon: GraduationCap, key: "Teachers" },
  { title: "Students", labelKey: "students", url: "/dashboard/users?tab=students", icon: UserRound, key: "Students" },
];

// Profile sub-items for admin (no payout). The parent expandable is
// labelled "Profile" — the "General" sub-item is the catch-all profile
// info form (name, email, etc).
const settingsSubItems = [
  { title: "General", labelKey: "general", url: "/dashboard/settings", icon: User },
  { title: "Profile Picture", labelKey: "profilePicture", url: "/dashboard/settings/profile-picture", icon: Camera },
];

// Admin sidebar navigation component
export function AdminSidebar({ side }: { side?: "left" | "right" }) {
  const t = useTranslations("Nav");
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const activeTab = searchParams?.get("tab") ?? null;
  const { data: session } = useSession();
  const sessionRole = session?.user?.role;
  const showFullAdminControls = isFullAdmin(sessionRole);
  const { handleLogout, isLoggingOut } = useLogout();

  const isSettingsPage = pathname === "/dashboard/settings" || pathname === "/dashboard/settings/profile-picture";
  const isUsersPage =
    pathname === "/dashboard/users" || pathname.startsWith("/dashboard/users/");
  const isSiteSettingsPage =
    pathname === "/dashboard/settings/platform" ||
    pathname.startsWith("/dashboard/settings/platform/") ||
    // The /dashboard/packages, /email-templates, /email-logs URLs are
    // kept as 308 redirects but the redirect happens before this check
    // gets to run — included here for safety in case a request lands
    // before the redirect (no observable rendering).
    pathname === "/dashboard/packages" ||
    pathname === "/dashboard/email-templates" ||
    pathname === "/dashboard/email-logs";
  // Profile group (the small one: General / Profile Picture) starts
  // collapsed unless you're inside it. Used to default open; the user
  // wants it closed so the bottom of the sidebar isn't visually noisy.
  const [settingsOpen, setSettingsOpen] = useState(isSettingsPage);
  const [usersOpen, setUsersOpen] = useState(isUsersPage);
  // Settings group (the long sub-list: General / Communication /
  // Financial Rules / Commission Tiers / Packages / Invites / Email
  // Templates / Email Logs / Audit) is collapsed by default unless
  // already on one of its destinations. Auto-opens via the effect
  // below; manual open/close persists for the rest of the session
  // (the dashboard layout stays mounted across client-side navs).
  const [siteSettingsOpen, setSiteSettingsOpen] = useState(isSiteSettingsPage);
  const userName = session?.user?.name || "Admin User";
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  const [pendingTeachers, setPendingTeachers] = useState(0);
  const [unreadFeedback, setUnreadFeedback] = useState(0);

  useEffect(() => {
    if (!session?.accessToken || session.user?.role !== "ADMIN") return;
    let cancelled = false;
    fetch("/api/withdrawals?status=PENDING&limit=100", {
      headers: { Authorization: `Bearer ${session.accessToken}` },
    })
      .then((r) => r.json())
      .then((res) => {
        if (cancelled) return;
        const count = res?.data?.withdrawals?.length ?? res?.data?.total ?? 0;
        setPendingWithdrawals(count);
      })
      .catch(() => {
        /* ignore */
      });
    fetch("/api/users?role=TEACHER&isActive=false&limit=1", {
      headers: { Authorization: `Bearer ${session.accessToken}` },
    })
      .then((r) => r.json())
      .then((res) => {
        if (cancelled) return;
        setPendingTeachers(res?.pagination?.total ?? 0);
      })
      .catch(() => {
        /* ignore */
      });
    fetch("/api/feedback/unread-count", {
      headers: { Authorization: `Bearer ${session.accessToken}` },
    })
      .then((r) => r.json())
      .then((res) => {
        if (cancelled) return;
        setUnreadFeedback(res?.data?.count ?? 0);
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [session?.accessToken, session?.user?.role, pathname]);

  // Auto-open Users group when navigating to any users path
  useEffect(() => {
    if (isUsersPage) setUsersOpen(true);
  }, [isUsersPage]);

  // Auto-open Site Settings group when on any of its destinations
  useEffect(() => {
    if (isSiteSettingsPage) setSiteSettingsOpen(true);
  }, [isSiteSettingsPage]);

  const renderPendingBadge = (key: "parent" | "teachers") => {
    if (pendingTeachers <= 0) return null;
    return (
      <span
        className="ms-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white"
        data-testid={
          key === "parent"
            ? "adminSidebarPendingTeachersBadge"
            : "adminSidebarPendingTeachersBadgeSubitem"
        }
      >
        {pendingTeachers}
      </span>
    );
  };

  return (
    <Sidebar side={side} className="mt-auto hidden h-[calc(100vh-4.5rem-var(--app-banner-h,0px))] border-r-0 border-l-0 bg-[#27335D] lg:block [&>div]:bg-[#27335D]">
      <SidebarContent className="bg-[#27335D]">
        {/* Navigation Menu */}
        <SidebarGroup className="flex-1 p-0">
          <SidebarGroupContent className="py-4 ps-4">
            <SidebarMenu>
              {/* Home (first) */}
              <SidebarMenuItem key="Home">
                <SidebarMenuButton className="rounded-none" asChild>
                  <a
                    href="/dashboard"
                    data-testid="adminSidebarLinkHome"
                    className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${
                      pathname === "/dashboard"
                        ? "rounded-s-lg bg-white text-[#27335D]"
                        : "text-white hover:rounded-s-lg hover:bg-white/10 hover:text-white/50"
                    }`}
                  >
                    <div className="me-3 h-5 w-5">
                      <Home className="h-5 w-5" />
                    </div>
                    <span className="flex-1 text-start">{t("home")}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Users (expandable) */}
              <SidebarMenuItem>
                <SidebarMenuButton className="rounded-none" asChild>
                  <button
                    onClick={() => setUsersOpen((o) => !o)}
                    data-testid="adminSidebarLinkUsers"
                    className={`flex w-full cursor-pointer items-center px-6 py-3 text-sm font-medium transition-colors ${
                      isUsersPage
                        ? "rounded-s-lg bg-white text-[#27335D]"
                        : "text-white hover:rounded-s-lg hover:bg-white/10 hover:text-white/50"
                    }`}
                  >
                    <div className="me-3 h-5 w-5">
                      <Users className="h-5 w-5" />
                    </div>
                    <span className="flex-1 text-start">{t("users")}</span>
                    {renderPendingBadge("parent")}
                    <ChevronDown
                      className={`ms-2 h-4 w-4 transition-transform ${usersOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {usersOpen &&
                usersSubItems.map((sub) => {
                  const active =
                    sub.url === "/dashboard/users"
                      ? pathname === "/dashboard/users"
                      : pathname === sub.url || pathname.startsWith(sub.url + "/");
                  return (
                    <SidebarMenuItem key={sub.title}>
                      <SidebarMenuButton className="rounded-none" asChild>
                        <a
                          href={sub.url}
                          data-testid={`adminSidebarLinkUsers${sub.key}`}
                          className={`flex items-center py-2 ps-14 pe-6 text-sm font-medium transition-colors ${
                            active
                              ? "rounded-s-lg bg-white/80 text-[#27335D]"
                              : "text-gray-300 hover:rounded-s-lg hover:bg-white/10 hover:text-white"
                          }`}
                        >
                          <div className="me-3 h-4 w-4">
                            <sub.icon className="h-4 w-4" />
                          </div>
                          <span className="flex-1 text-start">{t(sub.labelKey)}</span>
                          {sub.key === "Teachers" ? renderPendingBadge("teachers") : null}
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}

              {/* Grouped top-level items with a thin separator between
                  every group. Home + Users render above; a separator
                  sits before the operations group (visually dividing
                  Users from "Purchases / Bookings / Financials"), then
                  the content and system groups follow with their own
                  separators. Separator is rendered as a <li>
                  (non-focusable) to stay valid HTML inside the
                  surrounding <ul>. */}
              {adminMenuGroups
                .filter((group) => group.items.length > 0)
                .map((group) => (
                <React.Fragment key={group.id}>
                  <li
                    aria-hidden
                    data-testid={`adminSidebarSeparator-${group.id}`}
                    className="my-2 me-4 border-t border-white/15"
                  />
                  {group.items.filter((item) => !item.fullAdminOnly || showFullAdminControls).map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton className="rounded-none" asChild>
                        <a
                          href={item.url}
                          data-testid={`adminSidebarLink${item.title.replace(/\s+/g, "")}`}
                          className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${
                            pathname === item.url ||
                            pathname.startsWith(item.url + "/")
                              ? "rounded-s-lg bg-white text-[#27335D]"
                              : "text-white hover:rounded-s-lg hover:bg-white/10 hover:text-white/50"
                          }`}
                        >
                          <div className="me-3 h-5 w-5">
                            <item.icon className="h-5 w-5" />
                          </div>
                          <span className="flex-1 text-start">{t(item.labelKey)}</span>
                          {item.title === "Financials" && pendingWithdrawals > 0 ? (
                            <span
                              className="ms-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white"
                              data-testid="adminSidebarWithdrawalBadge"
                            >
                              {pendingWithdrawals}
                            </span>
                          ) : null}
                          {item.title === "Feedback" && unreadFeedback > 0 ? (
                            <span
                              className="ms-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white"
                              data-testid="adminSidebarFeedbackBadge"
                            >
                              {unreadFeedback}
                            </span>
                          ) : null}
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </React.Fragment>
              ))}

              {/* System / observability — full-admin only. Session replay
                  viewer (the empty "system" group's first real entry). */}
              {showFullAdminControls && (
                <>
                  <li
                    aria-hidden
                    data-testid="adminSidebarSeparator-system"
                    className="my-2 me-4 border-t border-white/15"
                  />
                  <SidebarMenuItem>
                    <SidebarMenuButton className="rounded-none" asChild>
                      <a
                        href="/dashboard/replays"
                        data-testid="adminSidebarLinkReplays"
                        className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${
                          pathname === "/dashboard/replays" ||
                          pathname.startsWith("/dashboard/replays/")
                            ? "rounded-s-lg bg-white text-[#27335D]"
                            : "text-white hover:rounded-s-lg hover:bg-white/10 hover:text-white/50"
                        }`}
                      >
                        <div className="me-3 h-5 w-5">
                          <MonitorPlay className="h-5 w-5" />
                        </div>
                        <span className="flex-1 text-start">{t("replays")}</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}

            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Bottom section: Configuration / Settings / Logout — all the
            "operator chrome" sits below the separator so the main top
            section stays focused on daily admin work. */}
        <SidebarGroup className="p-0">
          <SidebarGroupContent className="border-t border-gray-400 py-4 ps-4">
            <SidebarMenu>
              {/* Configuration (expandable). Sub-items are the Platform
                  Settings tabs exposed via `?tab=` plus the adjacent
                  admin pages (Packages, Email Templates, Email Logs).

                  Hidden for OPS_MANAGER per the role spec — Configuration
                  and everything under it is full-admin-only. */}
              {showFullAdminControls && (
                <>
                  <SidebarMenuItem>
                    <SidebarMenuButton className="rounded-none" asChild>
                      <button
                        onClick={() => setSiteSettingsOpen((o) => !o)}
                        data-testid="adminSidebarLinkSiteSettings"
                        className={`flex w-full cursor-pointer items-center px-6 py-3 text-sm font-medium transition-colors ${
                          isSiteSettingsPage
                            ? "rounded-s-lg bg-white text-[#27335D]"
                            : "text-white hover:rounded-s-lg hover:bg-white/10 hover:text-white/50"
                        }`}
                      >
                        <div className="me-3 h-5 w-5">
                          <Cog className="h-5 w-5" />
                        </div>
                        <span className="flex-1 text-start">{t("settings")}</span>
                        <ChevronDown
                          className={`ms-2 h-4 w-4 transition-transform ${siteSettingsOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                    </SidebarMenuButton>
                  </SidebarMenuItem>

                  {siteSettingsOpen &&
                    siteSettingsSubItems.map((sub) => {
                      const onSub =
                        pathname === sub.url || pathname.startsWith(sub.url + "/");
                      const tabActive = sub.tab
                        ? onSub && (activeTab ?? "general") === sub.tab
                        : onSub;
                      const href = sub.tab ? `${sub.url}?tab=${sub.tab}` : sub.url;
                      const testId = `adminSidebarLinkSiteSettings${sub.title.replace(/\s+/g, "")}`;
                      return (
                        <SidebarMenuItem key={sub.title}>
                          <SidebarMenuButton className="rounded-none" asChild>
                            <a
                              href={href}
                              data-testid={testId}
                              className={`flex items-center py-2 ps-14 pe-6 text-sm font-medium transition-colors ${
                                tabActive
                                  ? "rounded-s-lg bg-white/80 text-[#27335D]"
                                  : "text-gray-300 hover:rounded-s-lg hover:bg-white/10 hover:text-white"
                              }`}
                            >
                              <div className="me-3 h-4 w-4">
                                <sub.icon className="h-4 w-4" />
                              </div>
                              <span className="flex-1 text-start">{t(sub.labelKey)}</span>
                            </a>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                </>
              )}

              <SidebarMenuItem>
                <SidebarMenuButton className="rounded-none" asChild>
                  <button
                    onClick={() => setSettingsOpen((o) => !o)}
                    data-testid="adminSidebarLinkSettings"
                    className={`flex w-full cursor-pointer items-center px-6 py-3 text-sm font-medium transition-colors ${
                      isSettingsPage
                        ? "rounded-s-lg bg-white text-[#27335D]"
                        : "text-white hover:rounded-s-lg hover:bg-white/10 hover:text-white/50"
                    }`}
                  >
                    <div className="me-3 h-5 w-5">
                      <CircleUser className="h-5 w-5" />
                    </div>
                    <span className="flex-1 text-start">{t("profile")}</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${settingsOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {settingsOpen &&
                settingsSubItems.map((sub) => (
                  <SidebarMenuItem key={sub.title}>
                    <SidebarMenuButton className="rounded-none" asChild>
                      <a
                        href={sub.url}
                        data-testid={`adminSidebarLinkSettings${sub.title.replace(/\s+/g, "")}`}
                        className={`flex items-center py-2 ps-14 pe-6 text-sm font-medium transition-colors ${
                          pathname === sub.url
                            ? "rounded-s-lg bg-white/80 text-[#27335D]"
                            : "text-gray-300 hover:rounded-s-lg hover:bg-white/10 hover:text-white"
                        }`}
                      >
                        <div className="me-3 h-4 w-4">
                          <sub.icon className="h-4 w-4" />
                        </div>
                        {t(sub.labelKey)}
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}

              <SidebarMenuItem>
                <SidebarMenuButton className="rounded-none" asChild>
                  <button
                    onClick={() => handleLogout()}
                    disabled={isLoggingOut}
                    data-testid="adminSidebarButtonLogout"
                    className={`flex w-full items-center px-6 py-3 text-sm font-medium transition-colors ${
                      isLoggingOut
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer text-white hover:rounded-s-lg hover:bg-white/10 hover:text-white/50"
                    }`}
                  >
                    <div className="me-3 h-5 w-5">
                      <LogOut className="h-5 w-5" />
                    </div>
                    {isLoggingOut ? t("loggingOut") : t("logout")}
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
